import type {AiChatTargetSize, AiChatTargetSizeUnit, ChatMessageModelImagePart} from '@Components/ai-chat-window/ai-chat-window.types';
import {AI_CHAT_TARGET_SIZE_UNITS} from '@Components/ai-chat-window/ai-chat-window.types';
import {DownloadFileType, EXACT_SIZE_DOWNLOAD_FILE_TYPES, ORIGINAL_SIZE_DOWNLOAD_FILE_TYPES} from '@Components/ai-chat-window/lib/chat-button-actions.types';
import {isPlainObject} from '@Utils/object.util';

/** A pixel size has no dpi (TargetSize::printDpi() is null only for px), and names the pixels of the file it makes. */
export const formatTargetSizeLabel = (size: AiChatTargetSize): string => {
  if (size.dpi === null) {
    return window.i18next.t('pmwjs_ai_chat_size_label_px', {width: size.pixelWidth, height: size.pixelHeight});
  }

  return window.i18next.t('pmwjs_ai_chat_size_label_physical', {width: size.width, height: size.height, unit: size.unit, dpi: size.dpi});
};

/** TargetSize::DPI_STEPS[0]: physical sizes print at this unless the 12,000 px cap steps them down. */
const USUAL_PRINT_DPI = 300;

export const isReducedPrintDpi = (size: AiChatTargetSize): boolean => {
  return size.dpi !== null && size.dpi < USUAL_PRINT_DPI;
};

/** JPG flattens onto white, so a transparent image defaults to PNG. */
export const defaultFileTypeFor = (part: ChatMessageModelImagePart): DownloadFileType => {
  return part.hasTransparentBackground === true ? DownloadFileType.PNG : DownloadFileType.JPG;
};

/** JPG is only offered with a size; the original-size download serves PNG and PDF. */
export const isDownloadFileTypeAllowed = (fileType: unknown, targetSize: unknown): boolean => {
  if (targetSize === undefined) {
    return isAmong(ORIGINAL_SIZE_DOWNLOAD_FILE_TYPES, fileType);
  }

  return isAiChatTargetSize(targetSize) && isAmong(EXACT_SIZE_DOWNLOAD_FILE_TYPES, fileType);
};

const isAmong = (fileTypes: DownloadFileType[], fileType: unknown): boolean => fileTypes.some((allowed): boolean => allowed === fileType);

const isAiChatTargetSize = (value: unknown): value is AiChatTargetSize => {
  if (!isPlainObject(value)) {
    return false;
  }

  const hasDimensions = typeof value.width === 'number' && typeof value.height === 'number' && typeof value.pixelWidth === 'number' && typeof value.pixelHeight === 'number';
  const hasDpi = value.dpi === null || typeof value.dpi === 'number';

  const hasCompactForm = typeof value.compact === 'string' && value.compact !== '';

  return hasDimensions && hasDpi && hasCompactForm && isAiChatTargetSizeUnit(value.unit) && typeof value.requiresUpscale === 'boolean';
};

const isAiChatTargetSizeUnit = (unit: unknown): unit is AiChatTargetSizeUnit => {
  return AI_CHAT_TARGET_SIZE_UNITS.some((knownUnit): boolean => knownUnit === unit);
};
